import { forwardRef } from 'react';

import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';

import { cn } from '../utils';
import {
  checkboxBoxRecipe,
  checkboxControlRecipe,
  checkboxGlyphRecipe,
  checkboxLabelRecipe,
  checkboxRootRecipe,
} from './Checkbox.recipe';

import type { CheckboxProps } from './types';

/**
 * Design System 2.0 checkbox.
 *
 * Ported from Figma node 585:57 (file 5aSkE0U4TeWbuIZDvccHjc), tracking the 2026-09-15 rework. The
 * set's four variant axes are `State` (Default/Hover/Focus), `isChecked`, `isIndeterminate` and
 * `isDisabled`; of those only the last three are real inputs, because `State` is observed from the
 * DOM rather than passed — Base UI already tracks it. Its two non-variant component properties map
 * straight onto props: the `Label` text property is `label`, and the `Show Label` boolean is `label`
 * being undefined.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer, so
 * the same markup is correct in both themes.
 *
 * The wrapper is a real `<label>`, which is Base UI's documented shape for this component: the
 * hidden `<input>` lives inside `Checkbox.Root`, so wrapping both it and the text makes the text a
 * click target and gives the control its accessible name with no `id` plumbing. `Checkbox.Root`
 * itself renders a focusable `<span role="checkbox">` — the visible box is a child of it, since the
 * focus ring overflows the hit area and needs to hang off the box's own edge.
 *
 * Geometry, the state machine, the focus ring and why hover lives on the wrapper are all in
 * `Checkbox.recipe`.
 */
export const Checkbox = forwardRef<HTMLElement, CheckboxProps>(
  ({ label, className, rootClassName, labelClassName, disabled, ...rest }, ref) => (
    <label className={cn(checkboxRootRecipe({ disabled }), rootClassName)}>
      <BaseCheckbox.Root ref={ref} className={cn(checkboxControlRecipe(), className)} disabled={disabled} {...rest}>
        {/* `data-part` names the box so a test can assert on it without depending on a styling
            class, as `TextInput` does for its bordered box. */}
        <span className={checkboxBoxRecipe()} data-part="box" />

        {/**
         * Mounted only while the box is ticked or indeterminate, which Base UI handles.
         *
         * Both glyphs are always in the DOM and swapped in CSS rather than chosen from the
         * `indeterminate` prop, so a mixed state driven from somewhere we cannot see — a
         * `CheckboxGroup` parent, say — still draws the right one.
         *
         * ── The paths are Figma's, reconstructed rather than copied ──────────────────────────────
         *
         * Both glyphs are drawn in Figma as OUTLINED vectors — a stroke already converted to a fill,
         * so the exported path data is a 300-character bezier blob with the round caps baked in.
         * Pasting that would be unreadable and unmaintainable, so each one is reconstructed as the
         * stroke it was before outlining, in Figma's own 24x24 frame:
         *
         *   tick   an instance of the DS `Check` icon, 16px box scaled to 14px at (5, 5). Its
         *          centreline solves to 3 points and a 2.4009 stroke in the icon's own box; at
         *          14/16 that is the polyline and the 2.1 below. Reproduces the outline to within
         *          0.001px, which is exact for anything a browser can paint.
         *   dash   a 10x2 rounded rectangle at (7, 11), radius 1 — which is a 2px round-capped 8px
         *          line from (8, 12) to (16, 12), identically.
         *
         * NOTE for whoever next touches the icon set: no library in the repo can draw this tick.
         * `CheckIcon` in `src/icons` is still the Material glyph; the DS 2.0 library that landed in
         * #1161 (`components/Icon`) ships `CheckCircle` and `Checks` but no bare `Check`, and no
         * `Minus` for the dash; and Phosphor's own `Check` is the wrong weight — the Figma glyph's
         * stroke is roughly 2.4x Phosphor's at the same size. Until the DS glyph lands in one of
         * those sets, inlining is the only way to draw the tick that was actually drawn.
         */}
        <BaseCheckbox.Indicator className={checkboxGlyphRecipe()}>
          <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path
              className="group-data-[indeterminate]/cb:hidden"
              d="M16.2 8.85L10.32 15.15L7.8 12.45"
              data-part="tick"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.1"
            />
            <path
              className="hidden group-data-[indeterminate]/cb:block"
              d="M8 12H16"
              data-part="dash"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
            />
          </svg>
        </BaseCheckbox.Indicator>
      </BaseCheckbox.Root>

      {label !== undefined && <span className={cn(checkboxLabelRecipe({ disabled }), labelClassName)}>{label}</span>}
    </label>
  )
);
